import {
  ActionContext,
  ActionResult,
  ActionSpec,
} from '@/core/ambient-intelligence/types/ambient-intelligence.types';

// Validation result interface
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

// Action handler interface
export interface ActionHandler {
  execute(action: ActionSpec, context: ActionContext): Promise<ActionResult>;
  canHandle(action: ActionSpec): boolean;
  validate?(params: any): ValidationResult;
}

/**
 * ActionRegistry - Maps action types to their execution handlers
 * Allows new actions to be added without modifying core code
 */
class ActionRegistryClass {
  private handlers = new Map<string, ActionHandler>();

  /**
   * Register a new action handler
   */
  register(actionType: string, handler: ActionHandler): void {
    this.handlers.set(actionType, handler);
    console.log(`✅ Registered action handler: ${actionType}`);
  }

  /**
   * Execute any action
   */
  async execute(action: ActionSpec, context: ActionContext): Promise<ActionResult> {
    const handler = this.handlers.get(action.type);

    if (!handler) {
      console.warn(`No handler for action type: ${action.type}`);
      return {
        success: false,
        error: `Unknown action type: ${action.type}`,
      };
    }

    // Check if handler can handle this specific action
    if (!handler.canHandle(action)) {
      return {
        success: false,
        error: 'Handler cannot process this action',
      };
    }

    // Validate if validator exists
    if (handler.validate && action.params) {
      const validation = handler.validate(action.params);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error || 'Validation failed',
        };
      }
    }

    try {
      // Execute the action
      const result = await handler.execute(action, context);

      // Log successful execution
      console.log(`✓ Action executed: ${action.type}`, result);

      return result;
    } catch (error) {
      console.error(`Action execution failed: ${action.type}`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get all registered action types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Check if an action type has a handler
   */
  hasHandler(actionType: string): boolean {
    return this.handlers.has(actionType);
  }

  /**
   * Get handler for testing purposes
   */
  getHandler(actionType: string): ActionHandler | undefined {
    return this.handlers.get(actionType);
  }

  /**
   * Unregister an action type
   */
  unregister(actionType: string): void {
    this.handlers.delete(actionType);
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.handlers.clear();
  }
}

// Export singleton instance
export const ActionRegistry = new ActionRegistryClass();

// Export types
export type { ActionHandler, ValidationResult };
